export type MachineUserHeartbeat = {
  stamp?: string;
  user?: string | number | null;
  id?: string | number | null;
  state?: number | string | null;
  vx?: number | null;
};

export type MachineUserEvent = {
  stamp?: string;
  user?: string | number | null;
  id?: string | number | null;
  type?: string | null;
  category?: string | null;
  subcategory?: string | null;
  description?: string | null;
  severity?: string | null;
};

export type DailyPoint = { x: Date; y: number; label: string };
export type TimelineEntry = {
  kind: 'login' | 'operating' | 'break' | 'logout' | 'checklist' | 'blocked' | 'updating' | 'unknown';
  stamp: string;
  nextStamp?: string;
  machineId: string;
  state?: number | string | null;
  durationHours?: number;
};

export type TimelineRangeDataPoint = {
  x: number;
  y: [number, number];
  color?: string;
  label?: string;
  toolTipContent?: string;
};

export type MachineEventRisk = {
  machineId: string;
  crashes: number;
  holes: number;
  total: number;
};

export type MachineUserAnalytics = {
  dailyWorkHours: DailyPoint[];
  totalWorkHours: number;
  crashCount: number;
  holeCount: number;
  eventsPerDay: DailyPoint[];
  timeline: TimelineEntry[];
  machineSwitches: number;
  machineEventRisk: MachineEventRisk[];
  machinesWorked: number;
  firstLogin?: string;
  lastLogout?: string;
  longestBreakHours: number;
  averageShiftHours: number;
  workedDays: number;
  timelineRangePoints: TimelineRangeDataPoint[];
};

import { STATE_COLORS } from './machine-icon';

const OPERATING_STATE = 4;
const MAX_WORK_GAP_MS = 5 * 60 * 1000;
const OFF_STATE = 0;

function normalizeText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function normalizeUser(value: unknown): string {
  return normalizeText(value);
}

export function buildSelectedUserSet(selectedUser: string | number, selectedUserAliases: Array<string | number> = []): Set<string> {
  return new Set([selectedUser, ...selectedUserAliases]
    .map(normalizeUser)
    .filter(Boolean));
}

function parseStamp(stamp?: string): Date | null {
  if (!stamp) return null;
  const parsed = new Date(stamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function dayKeyFromDate(date: Date): string {
  // Operational calendar matches the summary table: Brasília (UTC-3).
  return new Date(date.getTime() - 3 * 3600000).toISOString().slice(0, 10);
}

function dayStartBrasilia(dayKey: string): Date {
  return new Date(`${dayKey}T00:00:00-03:00`);
}

function hoursBetween(start: Date, end: Date): number {
  return Math.max(0, (end.getTime() - start.getTime()) / 3600000);
}

function roundHours(hours: number): number {
  return Math.round(hours * 100) / 100;
}

function stateToKind(state: number | string | null | undefined): TimelineEntry['kind'] {
  const numeric = Number(state);
  switch (numeric) {
    case 0:
      return 'logout';
    case 1:
      return 'login';
    case 2:
      return 'checklist';
    case 3:
    case 5:
      return 'blocked';
    case 4:
      return 'operating';
    case 6:
      return 'updating';
    default:
      return 'unknown';
  }
}

export function isCrashEvent(event: Pick<MachineUserEvent, 'category' | 'subcategory' | 'description' | 'type'>): boolean {
  if (event.type === 'crash' || event.type === 'batida' || event.type === 'crash_fwd' || event.type === 'crash_rev') return true;
  const haystack = [event.category, event.subcategory, event.description, event.type]
    .map(normalizeText)
    .join(' ');
  return [
    'batida',
    'crash',
    'crash_fwd',
    'crash_rev',
    'colisao',
    'impacto',
    'impacto_horizontal',
    'impacto horizontal',
  ].some((token) => haystack.includes(token));
}

export function isHoleEvent(event: Pick<MachineUserEvent, 'category' | 'subcategory' | 'description' | 'type'>): boolean {
  if (event.type === 'hole' || event.type === 'buraco') return true;
  const haystack = [event.category, event.subcategory, event.description, event.type]
    .map(normalizeText)
    .join(' ');
  return ['buraco', 'hole', 'pothole', 'piso irregular'].some((token) => haystack.includes(token));
}

function addDurationToDayBuckets(start: Date, end: Date, dailyHours: Record<string, number>) {
  let cursor = new Date(start);

  while (cursor < end) {
    const key = dayKeyFromDate(cursor);
    const nextDay = dayStartBrasilia(key);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    const segmentEnd = end < nextDay ? end : nextDay;
    dailyHours[key] = (dailyHours[key] || 0) + hoursBetween(cursor, segmentEnd);
    cursor = segmentEnd;
  }
}

export function buildMachineUserAnalytics({
  selectedUser,
  selectedUserAliases = [],
  heartbeats,
  events,
  rangeStart,
  rangeEnd,
}: {
  selectedUser: string | number;
  selectedUserAliases?: Array<string | number>;
  heartbeats: MachineUserHeartbeat[];
  events: MachineUserEvent[];
  rangeStart?: Date | string;
  rangeEnd?: Date | string;
}): MachineUserAnalytics {
  const selectedUsers = buildSelectedUserSet(selectedUser, selectedUserAliases);

  const allHeartbeats = [...(heartbeats || [])]
    .map((hb) => ({ ...hb, parsedStamp: parseStamp(hb.stamp) }))
    .filter((hb): hb is MachineUserHeartbeat & { parsedStamp: Date } => Boolean(hb.parsedStamp))
    .sort((a, b) => a.parsedStamp.getTime() - b.parsedStamp.getTime());

  // Keep other operators' readings: a handover ends the previous user's time.
  const nextByMachine = new Map<typeof allHeartbeats[number], typeof allHeartbeats[number]>();
  const lastByMachine = new Map<string, typeof allHeartbeats[number]>();
  for (const heartbeat of allHeartbeats) {
    const machineId = String(heartbeat.id ?? 'Sem máquina');
    const previous = lastByMachine.get(machineId);
    if (previous) nextByMachine.set(previous, heartbeat);
    lastByMachine.set(machineId, heartbeat);
  }
  const selectedHeartbeats = allHeartbeats.filter((hb) => selectedUsers.has(normalizeUser(hb.user)));
  const startLimit = rangeStart ? new Date(rangeStart).getTime() : -Infinity;
  const endLimit = Math.min(rangeEnd ? new Date(rangeEnd).getTime() : Date.now(), Date.now());

  const dailyHours: Record<string, number> = {};
  const timeline: TimelineEntry[] = [];
  const machines = new Set<string>();
  let machineSwitches = 0;
  let longestBreakHours = 0;

  const timelineRangePoints: TimelineRangeDataPoint[] = [];

  for (let index = 0; index < selectedHeartbeats.length; index += 1) {
    const current = selectedHeartbeats[index];
    const next = selectedHeartbeats[index + 1];
    const currentState = Number(current.state);
    const machineId = String(current.id ?? 'Sem máquina');
    const nextMachineId = next ? String(next.id ?? 'Sem máquina') : undefined;

    const kind = stateToKind(current.state);
    const parsed = current.parsedStamp;
    const currentVx = typeof current.vx === 'number' ? current.vx : 0;

    let color = '#9ca3af'; // gray (idle)
    let stateLabel = 'Ocioso';

    if (currentState === 4) {
      color = STATE_COLORS["Operando"];
      stateLabel = 'Operando';
    } else if (currentState === 6) {
      color = STATE_COLORS["Atualizando"];
      stateLabel = 'Atualizando';
    } else if (currentState === 5) {
      color = STATE_COLORS["Bloqueado Remoto"];
      stateLabel = 'Bloqueado Remoto';
    } else if (currentState === 3) {
      color = STATE_COLORS["Bloqueada"];
      stateLabel = 'Bloqueada';
    } else if (currentState === 2) {
      color = STATE_COLORS["Checklist"];
      stateLabel = 'Checklist';
    } else if (currentState === 1) {
      color = STATE_COLORS["Login"];
      stateLabel = 'Login';
    } else if (currentState === 0) {
      color = STATE_COLORS["Desligado"];
      stateLabel = 'Desligado';
    } else if (currentVx > 0) {
      color = '#10b981'; // green (movement fallback)
      stateLabel = 'Em Movimento';
    }

    const nextMachineReading = nextByMachine.get(current);
    const intervalStart = Math.max(parsed.getTime(), startLimit);
    const intervalEnd = Math.max(intervalStart, Math.min(
      nextMachineReading?.parsedStamp.getTime() ?? Infinity,
      parsed.getTime() + MAX_WORK_GAP_MS,
      endLimit,
    ));

    if (intervalEnd > intervalStart) {
      timelineRangePoints.push({
        x: 2,
        y: [intervalStart, intervalEnd],
        color,
        label: 'Heartbeats',
        toolTipContent: `<strong>Heartbeat</strong><br/>Estado: ${stateLabel}<br/>Movimento: ${currentVx.toFixed(1)}<br/>Máquina: ${machineId}`,
      });
    }

    machines.add(machineId);

    if (currentState === OPERATING_STATE) {
      addDurationToDayBuckets(new Date(intervalStart), new Date(intervalEnd), dailyHours);
    }

    if (next && currentState === OFF_STATE) {
      const breakHours = roundHours(hoursBetween(current.parsedStamp, next.parsedStamp));
      longestBreakHours = Math.max(longestBreakHours, breakHours);
      timeline.push({
        kind: 'break',
        stamp: current.stamp || current.parsedStamp.toISOString(),
        nextStamp: next.stamp || next.parsedStamp.toISOString(),
        machineId,
        state: current.state,
        durationHours: breakHours,
      });
      if (nextMachineId && nextMachineId !== machineId) machineSwitches += 1;
      continue;
    }

    timeline.push({
      kind: stateToKind(current.state),
      stamp: current.stamp || current.parsedStamp.toISOString(),
      nextStamp: currentState === OPERATING_STATE ? new Date(intervalEnd).toISOString() : next?.stamp,
      machineId,
      state: current.state,
      durationHours: currentState === OPERATING_STATE
        ? roundHours((intervalEnd - intervalStart) / 3600000)
        : next ? roundHours(hoursBetween(current.parsedStamp, next.parsedStamp)) : undefined,
    });

    if (nextMachineId && nextMachineId !== machineId && currentState !== OFF_STATE) {
      machineSwitches += 1;
    }
  }

  const selectedEvents: Array<MachineUserEvent & { parsedStamp: Date }> = [];
  [...(events || [])].forEach((event) => {
    if (!selectedUsers.has(normalizeUser(event.user))) return;
    const parsedStamp = parseStamp(event.stamp);
    if (!parsedStamp) return;
    selectedEvents.push({ ...event, parsedStamp });
  });
  selectedEvents.sort((a, b) => a.parsedStamp.getTime() - b.parsedStamp.getTime());

  const crashByDay: Record<string, number> = {};
  const holeByDay: Record<string, number> = {};
  const riskByMachine: Record<string, MachineEventRisk> = {};
  let crashCount = 0;
  let holeCount = 0;

  selectedEvents.forEach((evt) => {
    const machineId = String(evt.id ?? 'Sem máquina');
    riskByMachine[machineId] = riskByMachine[machineId] || { machineId, crashes: 0, holes: 0, total: 0 };
    const day = dayKeyFromDate(evt.parsedStamp);
    const parsed = evt.parsedStamp;

    const isCrash = isCrashEvent(evt);
    const isHole = isHoleEvent(evt);

    let eventColor = '#9ca3af'; // gray (other events)
    let eventType = 'Outro';
    if (isCrash) {
      eventColor = '#ef4444'; // red
      eventType = 'Batida/Impacto';
      crashCount += 1;
      crashByDay[day] = (crashByDay[day] || 0) + 1;
      riskByMachine[machineId].crashes += 1;
      riskByMachine[machineId].total += 1;
    } else if (isHole) {
      eventColor = '#eab308'; // yellow
      eventType = 'Buraco';
      holeCount += 1;
      holeByDay[day] = (holeByDay[day] || 0) + 1;
      riskByMachine[machineId].holes += 1;
      riskByMachine[machineId].total += 1;
    } else if (evt.category?.toLowerCase() === 'checklist' || evt.subcategory?.toLowerCase() === 'checklist') {
      eventColor = '#3b82f6'; // blue
      eventType = 'Checklist';
    }

    timelineRangePoints.push({
      x: 1, // Row 1 for Events
      y: [parsed.getTime() - 60000, parsed.getTime() + 60000], // 2-minute wide block
      color: eventColor,
      label: 'Eventos',
      toolTipContent: `<strong>Evento: ${eventType}</strong><br/>${evt.category || ''} - ${evt.subcategory || ''}<br/>${evt.description || ''}<br/>Máquina: ${machineId}`
    });
  });

  const dailyWorkHours = Object.keys(dailyHours)
    .sort()
    .map((day) => ({ x: dayStartBrasilia(day), y: roundHours(dailyHours[day]), label: day }));

  const rawTotalWorkHours = Object.values(dailyHours).reduce((sum, hours) => sum + hours, 0);
  const totalWorkHours = roundHours(rawTotalWorkHours);

  const eventsPerDay = [
    ...Object.keys(crashByDay).sort().map((day) => ({ x: dayStartBrasilia(day), y: crashByDay[day], label: 'Batidas' })),
    ...Object.keys(holeByDay).sort().map((day) => ({ x: dayStartBrasilia(day), y: holeByDay[day], label: 'Buracos' })),
  ];

  const workDays = Object.values(dailyHours).filter((hours) => hours > 0).length;

  return {
    dailyWorkHours,
    totalWorkHours,
    crashCount,
    holeCount,
    eventsPerDay,
    timeline,
    machineSwitches,
    machineEventRisk: Object.values(riskByMachine)
      .filter((risk) => risk.total > 0)
      .sort((a, b) => b.total - a.total || a.machineId.localeCompare(b.machineId)),
    machinesWorked: machines.size,
    firstLogin: timeline.find((entry) => entry.kind !== 'break')?.stamp,
    lastLogout: [...timeline].reverse().find((entry) => entry.kind === 'logout' || entry.kind === 'break')?.stamp,
    longestBreakHours,
    averageShiftHours: workDays > 0 ? roundHours(rawTotalWorkHours / workDays) : 0,
    workedDays: workDays,
    timelineRangePoints,
  };
}

export function buildUserOptions(users: Array<{ login?: string | number; name?: string; active?: boolean }>): Array<{ value: string; label: string }> {
  return [...(users || [])]
    .filter((user) => user.active !== false)
    .map((user) => {
      const value = String(user.login ?? user.name ?? '');
      const label = user.name ? `${user.name} (${value})` : value;
      return { value, label };
    })
    .filter((option) => option.value.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label));
}
